import React, {
  useEffect,
  useState,
} from "react";

import "../styles/Payments.css";


/****************************************************
 * 💳 EYAL — RAZORPAY PAYMENT TEST PAGE
 ****************************************************/

const RAZORPAY_KEY_ID =
  process.env.REACT_APP_RAZORPAY_KEY_ID;


/****************************************************
 * FIREBASE PAYMENT FUNCTION
 ****************************************************/

const PAYMENT_FUNCTION_URL =
  "https://asia-south1-eyal--demo.cloudfunctions.net/paymentTest";


/****************************************************
 * LOAD RAZORPAY CHECKOUT
 ****************************************************/

function loadRazorpayScript() {

  return new Promise((resolve) => {

    /*
     * Already loaded
     */

    if (
      window.Razorpay
    ) {

      resolve(true);

      return;

    }


    const script =
      document.createElement("script");

    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";

    script.onload =
      () => resolve(true);

    script.onerror =
      () => resolve(false);

    document.body.appendChild(
      script
    );

  });

}


/****************************************************
 * COMPONENT
 ****************************************************/

export default function Payments() {

  const [
    amount,
    setAmount,
  ] = useState("");


  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    message,
    setMessage,
  ] = useState("");


  const [
    error,
    setError,
  ] = useState("");


  const [
    payment,
    setPayment,
  ] = useState(null);


  /**************************************************
   * LOAD RAZORPAY ON PAGE LOAD
   **************************************************/

  useEffect(() => {

    loadRazorpayScript();

  }, []);


  /**************************************************
   * FORMAT AMOUNT
   **************************************************/

  const getAmount =
    () => {

      const value =
        Number(amount);


      if (
        !Number.isFinite(value)
      ) {

        return 0;

      }


      return value;

    };


  /**************************************************
   * PAY
   **************************************************/

  const handlePayment =
    async () => {

      setMessage("");
      setError("");
      setPayment(null);


      const rupees =
        getAmount();


      /*
       * Validate amount
       */

      if (
        !rupees ||
        rupees <= 0
      ) {

        setError(
          "Please enter a valid amount."
        );

        return;

      }


      /*
       * Minimum test amount
       */

      if (
        rupees < 1
      ) {

        setError(
          "Minimum payment amount is ₹1."
        );

        return;

      }


      /*
       * Maximum test amount
       */

      if (
        rupees > 100000
      ) {

        setError(
          "Maximum test payment is ₹1,00,000."
        );

        return;

      }


      setLoading(true);


      try {

        /********************************************
         * 1. LOAD RAZORPAY
         ********************************************/

        const razorpayLoaded =
          await loadRazorpayScript();


        if (
          !razorpayLoaded
        ) {

          throw new Error(
            "Razorpay Checkout could not be loaded."
          );

        }


        /********************************************
         * 2. CHECK KEY
         ********************************************/

        if (
          !RAZORPAY_KEY_ID
        ) {

          throw new Error(
            "Razorpay Key ID is missing. Check your .env file."
          );

        }


        /********************************************
         * 3. CHECK BACKEND URL
         ********************************************/

        if (
          PAYMENT_FUNCTION_URL.includes(
            "YOUR_PROJECT_ID"
          )
        ) {

          throw new Error(
            "Firebase payment function URL is not configured yet."
          );

        }


        /********************************************
         * 4. CREATE RAZORPAY ORDER
         ********************************************/

        setMessage(
          "Creating payment order..."
        );


        const response =
          await fetch(
            `${PAYMENT_FUNCTION_URL}/createOrder`,
            {

              method:
                "POST",

              headers: {

                "Content-Type":
                  "application/json",

              },

              body:
                JSON.stringify({
                  amount:
                    rupees,
                }),

            }
          );


        const data =
          await response.json();


        if (
          !response.ok ||
          !data.success ||
          !data.order?.id
        ) {

          throw new Error(
            data.error ||
            "Unable to create Razorpay order."
          );

        }


        const order =
          data.order;


        /********************************************
         * 5. OPEN RAZORPAY CHECKOUT
         ********************************************/

        setMessage(
          "Opening Razorpay..."
        );


        const options = {

          key:
            RAZORPAY_KEY_ID,

          amount:
            order.amount,

          currency:
            order.currency ||
            "INR",

          name:
            "Eyal Mart",

          description:
            "Razorpay Payment Test",

          order_id:
            order.id,

          handler:
            async function (
              razorpayResponse
            ) {

              try {

                setLoading(true);

                setMessage(
                  "Verifying payment..."
                );

                setError("");


                /**********************************
                 * 6. VERIFY PAYMENT
                 **********************************/

                const verifyResponse =
                  await fetch(
                    `${PAYMENT_FUNCTION_URL}/verifyPayment`,
                    {

                      method:
                        "POST",

                      headers: {

                        "Content-Type":
                          "application/json",

                      },

                      body:
                        JSON.stringify({

                          razorpay_payment_id:
                            razorpayResponse
                              .razorpay_payment_id,

                          razorpay_order_id:
                            razorpayResponse
                              .razorpay_order_id,

                          razorpay_signature:
                            razorpayResponse
                              .razorpay_signature,

                        }),

                    }
                  );


                const verifyData =
                  await verifyResponse.json();


                if (
                  !verifyResponse.ok ||
                  !verifyData.success
                ) {

                  throw new Error(
                    verifyData.error ||
                    "Payment verification failed."
                  );

                }


                /********************************
                 * SUCCESS
                 ********************************/

                setPayment(
                  verifyData.payment
                );


                setMessage(
                  "Payment successful and verified!"
                );


                /*
                 * Clear amount
                 */

                setAmount("");


              } catch (verifyError) {

                console.error(
                  "Payment verification error:",
                  verifyError
                );


                setError(
                  verifyError.message ||
                  "Payment verification failed."
                );


                setMessage("");

              } finally {

                setLoading(false);

              }

            },


          prefill: {

            name:
              "Eyal User",

          },


          notes: {

            purpose:
              "Eyal Razorpay payment test",

          },


          theme: {

            color:
              "#0061ff",

          },


          modal: {

            ondismiss:
              function () {

                setLoading(false);

                setMessage("");

              },

          },

        };


        /********************************************
         * 7. OPEN CHECKOUT
         ********************************************/

        const razorpay =
          new window.Razorpay(
            options
          );


        razorpay.on(
          "payment.failed",
          function (
            response
          ) {

            console.error(
              "Razorpay payment failed:",
              response
            );


            setLoading(false);

            setMessage("");


            setError(
              response.error?.description ||
              "Payment failed. Please try again."
            );

          }
        );


        razorpay.open();


      } catch (err) {

        console.error(
          "Payment error:",
          err
        );


        setError(
          err.message ||
          "Unable to start payment."
        );


        setMessage("");


        setLoading(false);

      }

    };


  /**************************************************
   * UI
   **************************************************/

  return (

    <div className="payments-page">

      <div className="payments-card">


        {/* =========================================
            HEADER
        ========================================= */}

        <div className="payments-icon">

          💳

        </div>


        <h1>
          Eyal Payments
        </h1>


        <p className="payments-subtitle">
          Razorpay payment test
        </p>


        {/* =========================================
            AMOUNT
        ========================================= */}

        <div className="amount-section">

          <label htmlFor="paymentAmount">
            Enter Amount
          </label>


          <div className="amount-input-wrap">

            <span>
              ₹
            </span>


            <input
              id="paymentAmount"
              type="number"
              inputMode="decimal"
              min="1"
              max="100000"
              step="0.01"
              placeholder="100"
              value={amount}
              onChange={(e) =>
                setAmount(
                  e.target.value
                )
              }
              disabled={loading}
            />

          </div>

        </div>


        {/* =========================================
            PAY BUTTON
        ========================================= */}

        <button
          type="button"
          className="pay-button"
          onClick={handlePayment}
          disabled={
            loading ||
            !amount
          }
        >

          {loading
            ? "Processing..."
            : `Pay ₹${
                amount
                  ? Number(amount).toLocaleString(
                      "en-IN",
                      {
                        maximumFractionDigits: 2,
                      }
                    )
                  : "0"
              }`
          }

        </button>


        {/* =========================================
            MESSAGE
        ========================================= */}

        {message && (

          <div className="payment-message">

            <span>
              ⏳
            </span>

            {message}

          </div>

        )}


        {/* =========================================
            ERROR
        ========================================= */}

        {error && (

          <div className="payment-error">

            <span>
              ⚠️
            </span>

            {error}

          </div>

        )}


        {/* =========================================
            SUCCESS
        ========================================= */}

        {payment && (

          <div className="payment-success">

            <div className="success-icon">
              ✓
            </div>


            <h2>
              Payment Successful
            </h2>


            <div className="payment-details">

              <div className="payment-row">

                <span>
                  Amount
                </span>

                <strong>
                  ₹
                  {Number(
                    payment.amount
                  ).toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                    }
                  )}
                </strong>

              </div>


              <div className="payment-row">

                <span>
                  Payment ID
                </span>

                <strong>
                  {payment.razorpayPaymentId}
                </strong>

              </div>


              <div className="payment-row">

                <span>
                  Order ID
                </span>

                <strong>
                  {payment.razorpayOrderId}
                </strong>

              </div>


              <div className="payment-row">

                <span>
                  Status
                </span>

                <strong className="success-status">
                  {payment.status}
                </strong>

              </div>

            </div>


            <p className="verified-text">
              Payment verified by Eyal server.
            </p>

          </div>

        )}


        {/* =========================================
            TEST NOTICE
        ========================================= */}

        <div className="test-notice">

          <strong>
            🧪 Test Mode
          </strong>

          <span>
            This page is only for testing Razorpay.
            It does not add money to your wallet or
            create a shop order.
          </span>

        </div>

      </div>

    </div>

  );

}